import { Link } from "react-router-dom";
import type { Article } from "@/utils/content";
import { formatDate, formatViews, getCategoryName } from "@/utils/content";

type Variant = "grid" | "list" | "compact";

type Props = {
  article: Article;
  variant?: Variant;
  showCategory?: boolean;
};

export default function ArticleCard({ article, variant = "grid", showCategory = true }: Props) {
  if (variant === "compact") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex cursor-pointer items-center gap-3 rounded-lg p-2 transition-colors hover:bg-background-100"
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-sm font-bold text-primary-500">
          {String(article.id).replace("a", "")}
        </span>
        <span className="flex-1">
          <span className="line-clamp-2 text-sm font-medium text-foreground-800 transition-colors group-hover:text-primary-600">
            {article.title}
          </span>
          <span className="mt-1 flex items-center gap-2 text-xs text-foreground-400">
            <span>{formatDateShortLabel(article.date)}</span>
            <span>·</span>
            <span>{formatViews(article.views)}阅读</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "list") {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex cursor-pointer gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-all duration-200 hover:border-primary-300 md:gap-5 md:p-4"
      >
        <div className="h-24 w-36 shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-28 sm:w-44">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 噜噜社`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col justify-between py-1">
          <div>
            {showCategory && (
              <span className="mb-2 inline-flex rounded-full bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                {getCategoryName(article.category)}
              </span>
            )}
            <h3 className="line-clamp-2 text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
              {article.title}
            </h3>
            <p className="mt-2 hidden text-sm leading-relaxed text-foreground-500 line-clamp-2 sm:block">
              {article.summary}
            </p>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-400">
            <span>{article.author}</span>
            <span>{formatDate(article.date)}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-200 hover:border-primary-300"
    >
      <div className="h-44 w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 噜噜社`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-4">
        {showCategory && (
          <span className="mb-2 inline-flex w-fit rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-900">
            {getCategoryName(article.category)}
          </span>
        )}
        <h3 className="line-clamp-2 text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-500">{article.summary}</p>
        <div className="mt-3 flex items-center justify-between text-xs text-foreground-400">
          <span>{article.author}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}

function formatDateShortLabel(date: string): string {
  const [, m, d] = date.split("-");
  return m && d ? `${m}/${d}` : date;
}